import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { FONT, NEUTRAL } from "../../styles/brandTokens";
import { DirectorySource, DirectoryUser } from "./customerApprovers";

interface DirectoryUserPickerProps {
  label: string;
  helperText: string;
  customerId: string;
  directory: DirectorySource;
  value: readonly DirectoryUser[];
  onChange: (users: DirectoryUser[]) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/** Multi-select over the customer's directory. The directory does the filtering, so options are shown as returned. */
export default function DirectoryUserPicker({ label, helperText, customerId, directory, value, onChange }: DirectoryUserPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<DirectoryUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    let isActive = true;
    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const users = await directory.searchUsers(customerId, query.trim());
        if (isActive) {
          setOptions(users);
          setError("");
        }
      } catch {
        if (isActive) {
          setOptions([]);
          setError("Couldn't search this customer's directory. Try again in a moment.");
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [customerId, directory, isOpen, query]);

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={options}
      value={value as DirectoryUser[]}
      loading={isLoading}
      filterOptions={(candidates) => candidates}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(user) => user.name}
      getOptionKey={(user) => user.id}
      onChange={(_, users) => onChange(users)}
      onInputChange={(_, text, reason) => {
        if (reason === "input" || reason === "clear") setQuery(text);
      }}
      noOptionsText={query.trim() ? "No matching users" : "Type a name or email"}
      renderOption={(props, user) => (
        <Box component="li" {...props} key={user.id} sx={{ display: "grid !important", gap: 0.25 }}>
          <Typography sx={{ fontFamily: FONT.sans, fontSize: "14px", color: NEUTRAL.text }}>{user.name}</Typography>
          <Typography sx={{ fontFamily: FONT.sans, fontSize: "12.5px", color: NEUTRAL.textMuted }}>{user.email}</Typography>
        </Box>
      )}
      renderTags={(users, getTagProps) =>
        users.map((user, index) => (
          <Chip
            {...getTagProps({ index })}
            key={user.id}
            size="small"
            label={`${user.name} · ${user.email}`}
            sx={{ fontFamily: FONT.sans, maxWidth: "100%" }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? "Search by name or email" : undefined}
          error={Boolean(error)}
          helperText={error || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
